import { useState } from "react";
import { useAuth } from "@/modules/auth";
import { useRegularisationPolicy } from "@/api/hooks/regularisations";
import { AlertTriangle, X } from "@/shared/icons";

// Roles that file their own regularisations — the people the limit binds.
const SUBMITTER_ROLES = new Set(["admin", "employee", "operations"]);

const STORAGE_KEY = "workman.regPolicyBanner.dismissed";

function readDismissed(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeDismissed(value: string) {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Private mode / blocked storage — the banner just comes back next load.
  }
}

/**
 * App-wide strip announcing the monthly regularisation limit (2026-11-02).
 * Technical Admin switches it on/off in Regularisations > Policy; the text
 * comes from the server and tracks the live limit. Dismissal is per browser
 * and keyed on the policy version, so changing the limit shows it again.
 */
export function RegularisationPolicyBanner() {
  const { user } = useAuth();
  const eligible = !!user && SUBMITTER_ROLES.has(user.role);
  const policy = useRegularisationPolicy(eligible).data;
  const [dismissed, setDismissed] = useState<string | null>(readDismissed);

  if (!eligible || !policy || !policy.quotaEnabled || !policy.bannerEnabled) return null;
  const version = `${policy.monthlyLimit}|${policy.updatedAt ?? "default"}`;
  if (dismissed === version) return null;

  return (
    <div
      role="status"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "10px 24px",
        borderBottom: "1px solid var(--warn-line, #f0d9a8)",
        background: "var(--warn-soft, #fdf8ec)",
        color: "var(--warn-ink, #7a5510)",
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 2 }} />
      <span style={{ flex: 1 }}>
        <strong>Regularisation policy:</strong> {policy.message}
      </span>
      <button
        type="button"
        onClick={() => {
          writeDismissed(version);
          setDismissed(version);
        }}
        aria-label="Dismiss"
        style={{
          display: "inline-flex",
          border: "none",
          background: "none",
          cursor: "pointer",
          padding: 2,
          color: "inherit",
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
}
